import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiFetch } from "../../api/client";
import { PageFooter } from "../../app/PageFooter";
import { PATHS } from "../../app/paths";
import { TopBar } from "../../app/TopBar";
import { useAppUser } from "../../app/AppUserContext";
import { Btn } from "../../components/ui/Button";
import { Chip } from "../../components/ui/Chip";
import { FacilityDetailDialog } from "./FacilityDetailDialog";
import { FacilityFiltersBar } from "./FacilityFiltersBar";
import { FacilityTable } from "./FacilityTable";
import { FacilityUploadDialog } from "./FacilityUploadDialog";
import { MatchQueue } from "./MatchQueue";
import { RefreshBanner } from "./RefreshBanner";
import { RefreshPanel } from "./RefreshPanel";
import { justFinished } from "./refreshStatus";
import { useRefreshStatus } from "./useRefreshStatus";
import { EMPTY_FILTERS, formatBbox, listPath, type Bbox, type FacilityFilters } from "./query";
import { exportVerified, type ExportOutcome } from "./reviewApi";
import { useDebouncedValue } from "./useDebouncedValue";
import { errorMessage } from "../cockpit/format";
import type { FacilityMatchPage, FrsRefreshStatus, FrsRefreshView } from "./types";

const FacilityMapView = lazy(() =>
  import("./FacilityMapView").then((module) => ({ default: module.FacilityMapView })),
);

const TABS = [
  { key: "registry", label: "Registry", icon: "table_view" },
  { key: "map", label: "Map", icon: "map" },
  { key: "matches", label: "Supplier Matches", icon: "link" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

const SEARCH_DEBOUNCE_MS = 350;

function parseTab(value: string | null): TabKey {
  return TABS.find((t) => t.key === value)?.key ?? "registry";
}

export function FacilitiesPage() {
  const navigate = useNavigate();
  const { appRole } = useAppUser();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"));

  const [filters, setFilters] = useState<FacilityFilters>(EMPTY_FILTERS);
  const applied = useDebouncedValue(filters, SEARCH_DEBOUNCE_MS);
  const [openRegistryId, setOpenRegistryId] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [finishedRefreshes, setFinishedRefreshes] = useState(0);
  const [pending, setPending] = useState<number | null>(null);
  const [reviews, setReviews] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<ExportOutcome | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const canReview = appRole === "Uploader" || appRole === "Admin";
  const { status, elapsedSeconds, unreachable, reload } = useRefreshStatus();
  const previous = useRef<FrsRefreshStatus | null>(null);
  const [result, setResult] = useState<FrsRefreshView | null>(null);

  useEffect(() => {
    if (!status) return;
    if (justFinished(previous.current, status)) {
      setVersion((v) => v + 1);
      setFinishedRefreshes((n) => n + 1);
      setResult(status.problem ? null : status.lastRun);
    }
    previous.current = status;
  }, [status]);

  const loadPending = useCallback(async () => {
    try {
      const response = await apiFetchRef.current("facilities/matches?matchState=Pending&hideUnmatched=true&limit=1");
      const body: FacilityMatchPage = await response.json();
      setPending(body.total);
    } catch {
      setPending(null);
    }
  }, []);

  useEffect(() => {
    void loadPending();
  }, [loadPending, version, reviews]);

  const selectTab = (key: TabKey) => setSearchParams(key === "registry" ? {} : { tab: key });
  const closeDetail = useCallback(() => setOpenRegistryId(null), []);
  const onDecided = useCallback(() => setVersion((v) => v + 1), []);
  const onReviewed = useCallback(() => setReviews((n) => n + 1), []);
  const onDetailChanged = useCallback(() => {
    setReviews((n) => n + 1);
    setVersion((v) => v + 1);
  }, []);

  const runExport = async () => {
    setExporting(true);
    setExported(null);
    setExportError(null);
    try {
      setExported(await exportVerified(apiFetchRef.current));
    } catch (err) {
      setExportError(errorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <TopBar title="EPA Plant Registry">
        <Btn variant="ghost" onClick={() => navigate(PATHS.polymerGroups)}>
          <span className="material-symbols-sharp">category</span>Polymer Groups
        </Btn>
        <Btn variant="ghost" onClick={() => navigate(PATHS.intakeLane("spend"))}>
          <span className="material-symbols-sharp">arrow_back</span>Data Intake
        </Btn>
      </TopBar>

      <nav className="flex bg-rs-black px-7 border-b border-rs-jet sticky top-[59px] z-[19] overflow-x-auto" role="tablist" aria-label="Plant registry">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`bg-transparent border-none border-b-2 py-[13px] mr-[30px] whitespace-nowrap flex items-center gap-2 ${
              tab === t.key ? "text-white border-b-rs-green" : "text-rs-light-gray border-b-transparent hover:text-rs-off-white"
            }`}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => selectTab(t.key)}
          >
            <span className="material-symbols-sharp">{t.icon}</span>
            {t.label}
            {t.key === "matches" && pending !== null && pending > 0 && (
              <span className="text-[11px] rounded-[10px] py-px px-[7px] bg-rs-jet text-rs-light-gray">
                {pending.toLocaleString()}
              </span>
            )}
          </button>
        ))}
      </nav>

      <main className="pt-[26px] px-7 pb-[70px]">
        <RefreshBanner
          status={status}
          elapsedSeconds={elapsedSeconds}
          unreachable={unreachable}
          result={result}
          onDismissResult={() => setResult(null)}
        />
        <RefreshPanel
          isAdmin={appRole === "Admin"}
          version={version + reviews}
          status={status}
          onQueued={() => void reload()}
        />

        {canReview && tab !== "matches" && (
          <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
            {exported && (
              <span className="mr-auto text-[13px] text-rs-light-gray" role="status">
                Exported {exported.exported.toLocaleString()} verified{" "}
                {exported.exported === 1 ? "facility" : "facilities"} to {exported.fileName}
                {exported.skipped > 0
                  ? `. ${exported.skipped.toLocaleString()} left out because they have no coordinates.`
                  : "."}
              </span>
            )}
            {exportError && <span className="mr-auto text-[13px] text-rs-red">{exportError}</span>}
            <Btn variant="ghost" disabled={exporting} onClick={() => void runExport()}>
              <span className="material-symbols-sharp">download</span>Export Verified
            </Btn>
            <Btn variant="ghost" onClick={() => setUploading(true)}>
              <span className="material-symbols-sharp">upload_file</span>Upload Facilities
            </Btn>
          </div>
        )}

        {tab !== "matches" && (
          <>
            <FacilityFiltersBar filters={filters} onChange={setFilters} />
            {filters.bbox && (
              <p className="m-0 mb-3 text-[13px] text-rs-light-gray">
                <Chip variant="info">Region {formatBbox(filters.bbox)}</Chip>{" "}
                <button
                  className="bg-transparent border-none p-0 ml-2 text-rs-green"
                  onClick={() => setFilters({ ...filters, bbox: null })}
                >
                  Clear Region
                </button>
              </p>
            )}
          </>
        )}

        {tab === "registry" && <FacilityTable
            key={`${listPath(applied, 0, 0)}#${version}`}
            filters={applied}
            canReview={canReview}
            onOpen={setOpenRegistryId}
            onReviewed={onReviewed}
          />}
        {tab === "map" && (
          <Suspense fallback={<p className="text-rs-light-gray text-[13px]">Loading map…</p>}>
          <FacilityMapView
            key={version}
            filters={applied}
            onSelectRegion={(bbox: Bbox) => setFilters({ ...filters, bbox })}
            onClearRegion={() => setFilters({ ...filters, bbox: null })}
            onOpen={setOpenRegistryId}
          />
          </Suspense>
        )}
        {tab === "matches" && <MatchQueue onDecided={onDecided} onOpen={setOpenRegistryId} reloadKey={finishedRefreshes} />}
      </main>

      {openRegistryId && (
        <FacilityDetailDialog
          registryId={openRegistryId}
          canReview={canReview}
          onClose={closeDetail}
          onChanged={onDetailChanged}
        />
      )}
      {uploading && (
        <FacilityUploadDialog onClose={() => setUploading(false)} onImported={() => setVersion((v) => v + 1)} />
      )}
      <PageFooter />
    </>
  );
}
